import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { AlertRuleViewStore } from "../../store";
import { AlertRuleView } from "../view/AlertRuleView";
import { AlertRuleUpdateStore } from "../../store";
import { AlertRuleUpdate } from "../update/AlertRuleUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type AlertRuleModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const AlertRuleModalContent: React.FC<AlertRuleModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new AlertRuleUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new AlertRuleViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <AlertRuleView
            alertRuleViewStore={storeInfo.store as AlertRuleViewStore}
          />
        )}
        {storeInfo.type === "update" && (
          <AlertRuleUpdate
            alertRuleUpdateStore={storeInfo.store as AlertRuleUpdateStore}
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
